/** Localized formatting in the ranch's units. Stored values keep their original unit. */
import { useMemo } from "react";
import { UNIT_LABELS, convertUnit, preferredUnit, UNITS, type Dimension, type Formatters, type Precision } from "../../contract";
import { getLocale, plural, t, useLanguage } from "./i18n";
import { useMe } from "./session";

export function formatDate(value: string | null | undefined, precision: Precision | string = "date", opts: { weekday?: boolean } = {}): string {
  if (!value) return t("Unknown date");
  const locale = getLocale();
  if (precision === "year" || /^\d{4}$/.test(value)) return value.slice(0, 4);
  if (precision === "month" || /^\d{4}-\d{2}$/.test(value)) return new Date(`${value.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString(locale, { month: "long", year: "numeric", timeZone: "UTC" });
  const d = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  const base = d.toLocaleDateString(locale, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }), ...(opts.weekday ? { weekday: "short" } : {}), timeZone: value.length === 10 ? "UTC" : undefined });
  if (precision === "minute" && value.length > 10) return `${base}, ${d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}`;
  if (precision === "estimated_date") return t("about {{date}}", { date: base });
  return base;
}

/** "Today", "Yesterday", "3 days ago", or a date. */
export function relativeDay(value: string | null | undefined, today: string): string {
  if (!value) return t("Unknown date");
  const days = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${value.slice(0, 10)}T12:00:00Z`)) / 86_400_000);
  if (days === 0) return t("Today");
  if (days === 1) return t("Yesterday");
  if (days === -1) return t("Tomorrow");
  if (days > 1 && days < 7) return plural(days, "{{count}} day ago", "{{count}} days ago");
  if (days < -1 && days > -7) return plural(-days, "In {{count}} day", "In {{count}} days");
  return formatDate(value);
}

export function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return t("just now");
  if (s < 3600) return plural(Math.floor(s / 60), "{{count}} min ago", "{{count}} min ago");
  if (s < 86_400) return plural(Math.floor(s / 3600), "{{count}} hr ago", "{{count}} hr ago");
  return formatDate(iso);
}

export function formatAge(days: number | null | undefined): string {
  if (days === null || days === undefined || days < 0) return "";
  if (days < 60) return plural(Math.max(days, 0), "{{count}} day", "{{count}} days");
  if (days < 730) return plural(Math.floor(days / 30.44), "{{count}} mo", "{{count}} mo");
  return plural(Math.floor(days / 365.25), "{{count}} yr", "{{count}} yr");
}

function dimensionOf(unit: string): Dimension | null {
  for (const [dim, units] of Object.entries(UNITS)) if (units.includes(unit)) return dim as Dimension;
  return null;
}

export function formatNumber(n: number, digits = 1): string {
  return n.toLocaleString(getLocale(), { maximumFractionDigits: digits });
}

export function formatQuantity(q: { value: number; unit: string } | null | undefined, system: "imperial" | "metric" = "imperial", opts: { convert?: boolean } = {}): string {
  if (!q) return "";
  let { value, unit } = q;
  const dim = dimensionOf(unit);
  if (opts.convert !== false && dim) {
    const target = preferredUnit(dim, system);
    const converted = target ? convertUnit(value, unit, target) : null;
    if (target && converted !== null && (dim === "weight" || dim === "area" || dim === "temperature")) { value = converted; unit = target; }
  }
  return `${formatNumber(value, value >= 100 ? 0 : 1)} ${t(UNIT_LABELS[unit] ?? unit)}`;
}

export function formatMoney(m: { minor: number; currency: string } | null | undefined): string {
  if (!m) return "";
  return (m.minor / 100).toLocaleString(getLocale(), { style: "currency", currency: m.currency || "USD" });
}

export function makeFormatters(labels: Record<string, string>, system: "imperial" | "metric" = "imperial"): Formatters {
  return {
    t,
    date: (v, p) => formatDate(v, p ?? "date"),
    quantity: (q) => formatQuantity(q, system),
    money: formatMoney,
    label: (id) => (id ? labels[id] ?? labels[`new:${id.replace(/^new:/, "")}`] ?? "…" : ""),
    list: (items) => new Intl.ListFormat(getLocale(), { style: "short", type: "conjunction" }).format(items.filter(Boolean)),
    count: (n, one, other) => plural(n, one, other),
  };
}

/** Formatters bound to the current language and the ranch's unit system. */
export function useFormat(labels: Record<string, string> = {}): Formatters {
  const lang = useLanguage();
  const { data } = useMe();
  const units = data?.ranch?.units ?? "imperial";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => makeFormatters(labels, units), [lang, units, labels]);
}
